import React, { useEffect, useState } from "react";

// EyeDropper API browser support note:
// - Supported: Chrome 95+, Edge 95+
// - Not supported: Firefox, Safari
//   (in those browsers we fall back to sampling pixels from the Fabric canvas).

// Helper: convert RGB values (0–255) to HEX string.
const rgbToHex = (r, g, b) => {
  const toHex = (v) => {
    const n = Math.max(0, Math.min(255, Number(v) || 0));
    return n.toString(16).padStart(2, "0");
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Helper: attempt to normalize common color formats to HEX.
const normalizeToHex = (value) => {
  if (!value || typeof value !== "string") return null;

  const color = value.trim();

  if (color[0] === "#" && (color.length === 4 || color.length === 7)) {
    // Already HEX (#rgb or #rrggbb)
    if (color.length === 4) {
      const r = color[1];
      const g = color[2];
      const b = color[3];
      return `#${r}${r}${g}${g}${b}${b}`;
    }
    return color.toLowerCase();
  }

  // rgb(...) or rgba(...)
  const rgbMatch = color.match(/rgba?\(([^)]+)\)/i);
  if (rgbMatch) {
    const parts = rgbMatch[1].split(",").map((p) => parseFloat(p.trim()));
    const [r, g, b] = parts;
    return rgbToHex(r, g, b);
  }

  return null;
};

// Extract a HEX color from the underlying canvas pixels.
const pickPixelColorFromCanvas = (canvas, pointer) => {
  if (!canvas || !pointer || !canvas.contextContainer) return null;

  const ctx = canvas.contextContainer;
  const backingCanvas = ctx.canvas;

  if (!backingCanvas) return null;

  const logicalWidth = canvas.getWidth() || backingCanvas.width;
  const logicalHeight = canvas.getHeight() || backingCanvas.height;

  const scaleX = backingCanvas.width / logicalWidth || 1;
  const scaleY = backingCanvas.height / logicalHeight || 1;

  const x = Math.round(pointer.x * scaleX);
  const y = Math.round(pointer.y * scaleY);

  if (
    x < 0 ||
    y < 0 ||
    x >= backingCanvas.width ||
    y >= backingCanvas.height
  ) {
    return null;
  }

  try {
    const pixel = ctx.getImageData(x, y, 1, 1).data;
    const [r, g, b, a] = pixel;

    // If the pixel is fully transparent, treat as "no color".
    if (a === 0) return null;

    return rgbToHex(r, g, b);
  } catch (error) {
    // getImageData can throw for security / tainted canvas issues.
    return null;
  }
};

// Decide which color to use: object color (fill/stroke) when available,
// otherwise fall back to raw pixel color under the cursor.
const resolvePickedColor = ({ canvas, pointer, target, mode }) => {
  let objectColor = null;

  if (target) {
    if (mode === "stroke" && target.stroke) {
      objectColor = normalizeToHex(target.stroke);
    } else if (target.fill) {
      objectColor = normalizeToHex(target.fill);
    }
  }

  const pixelColor = pickPixelColorFromCanvas(canvas, pointer);

  // Prefer explicit object color when available; otherwise use the pixel color.
  return objectColor || pixelColor;
};

// Apply selected color to the currently active Fabric object (if any).
const applyColorToActiveObject = (canvas, hexColor, mode) => {
  if (!canvas || !hexColor) return;

  const activeObject = canvas.getActiveObject();
  if (!activeObject) return;

  const applyToObject = (obj) => {
    if (mode === "stroke") {
      obj.set("stroke", hexColor);
      if (obj.fromSVG !== undefined) {
        obj.set("strokeWidth", obj.strokeWidth || 5);
      }
    } else {
      obj.set("fill", hexColor);
    }
  };

  if (activeObject.type === "group" && activeObject.forEachObject) {
    activeObject.forEachObject((child) => applyToObject(child));
  } else {
    applyToObject(activeObject);
  }

  canvas.requestRenderAll();
  if (typeof canvas.fire === "function") canvas.fire("object:modified");
};

/**
 * Canva-style eyedropper / color picker pencil for Fabric.js.
 *
 * Props:
 * - Canvaseditor: Fabric.Canvas instance
 * - mode: "fill" | "stroke" (default: "fill")
 * - onColorPicked(hex): callback when a color is chosen
 * - applyToActiveObject: whether to auto-apply color to active Fabric object
 *   (default: true). Set to false when the parent component wants to control
 *   how the picked color is applied (e.g. background, shadow, etc.).
 */
export function ColorPickerPencil({
  Canvaseditor,
  mode = "fill",
  onColorPicked,
  applyToActiveObject = true,
}) {
  const [isEyedropperActive, setIsEyedropperActive] = useState(false);

  // Primary click handler: try native EyeDropper first, then fallback.
  const handleButtonClick = async () => {
    if (!Canvaseditor) return;

    // Primary: native EyeDropper API (Chrome/Edge)
    if (typeof window !== "undefined" && "EyeDropper" in window) {
      try {
        setIsEyedropperActive(true);
        // eslint-disable-next-line no-undef
        const eyeDropper = new window.EyeDropper();
        const result = await eyeDropper.open();

        if (result && result.sRGBHex) {
          const hexColor = result.sRGBHex;

          if (typeof onColorPicked === "function") {
            onColorPicked(hexColor);
          }

          if (applyToActiveObject) {
            applyColorToActiveObject(Canvaseditor, hexColor, mode);
          }
        }
      } catch (error) {
        // User cancelled (e.g. pressed Escape or clicked elsewhere) – no action needed.
      } finally {
        setIsEyedropperActive(false);
      }

      return;
    }

    // Fallback: browsers without EyeDropper API, use Fabric canvas pixel sampling.
    setIsEyedropperActive((prev) => !prev);
  };

  useEffect(() => {
    const canvas = Canvaseditor;
    if (!canvas || !isEyedropperActive) {
      return;
    }

    // Only run this effect as the fallback path when EyeDropper API is NOT available.
    if (typeof window !== "undefined" && "EyeDropper" in window) {
      return;
    }

    // Store previous interaction state so we can restore it afterwards.
    const previousSelection = canvas.selection;
    const previousSkipTargetFind = canvas.skipTargetFind;
    const previousDefaultCursor = canvas.defaultCursor;
    const previousHoverCursor = canvas.hoverCursor;

    canvas.selection = false;
    canvas.skipTargetFind = true;
    canvas.defaultCursor = "crosshair";
    canvas.hoverCursor = "crosshair";

    const exitEyedropper = () => {
      canvas.selection = previousSelection;
      canvas.skipTargetFind = previousSkipTargetFind;
      canvas.defaultCursor = previousDefaultCursor;
      canvas.hoverCursor = previousHoverCursor;
      canvas.off("mouse:move", handleMouseMove);
      canvas.off("mouse:down", handleMouseDown);
      window.removeEventListener("keydown", handleKeyDown);
      setIsEyedropperActive(false);
    };

    const handleMouseMove = (opt) => {
      const pointer =
        opt?.absolutePointer ||
        opt?.pointer ||
        (opt?.e ? canvas.getPointer(opt.e) : null);
      if (!pointer) return;

      // Live preview could be added here if needed in future.
      pickPixelColorFromCanvas(canvas, pointer);
    };

    const handleMouseDown = (opt) => {
      const pointer =
        opt?.absolutePointer ||
        opt?.pointer ||
        (opt?.e ? canvas.getPointer(opt.e) : null);
      if (!pointer) {
        exitEyedropper();
        return;
      }

      const target = opt?.target || null;
      const hexColor = resolvePickedColor({
        canvas,
        pointer,
        target,
        mode,
      });

      if (hexColor) {
        if (typeof onColorPicked === "function") {
          onColorPicked(hexColor);
        }
        if (applyToActiveObject) {
          applyColorToActiveObject(canvas, hexColor, mode);
        }
      }

      exitEyedropper();
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        exitEyedropper();
      }
    };

    canvas.on("mouse:move", handleMouseMove);
    canvas.on("mouse:down", handleMouseDown);
    window.addEventListener("keydown", handleKeyDown);

    // Cleanup when component unmounts or when eyedropper deactivates.
    return () => {
      canvas.off("mouse:move", handleMouseMove);
      canvas.off("mouse:down", handleMouseDown);
      window.removeEventListener("keydown", handleKeyDown);
      canvas.selection = previousSelection;
      canvas.skipTargetFind = previousSkipTargetFind;
      canvas.defaultCursor = previousDefaultCursor;
      canvas.hoverCursor = previousHoverCursor;
    };
  }, [Canvaseditor, isEyedropperActive, mode, onColorPicked]);

  return (
    <div
      className="eyedropper-container"
      style={{ display: "inline-flex", alignItems: "center", flexShrink: 0, flexGrow: 0 }}
    >
      <button
        type="button"
        onClick={handleButtonClick}
        className={isEyedropperActive ? "eyedropper-button active" : "eyedropper-button"}
        title={isEyedropperActive ? "Eyedropper active – ESC to cancel" : "Pick color from canvas"}
        style={{
          border: "1px solid #ced4da",
          backgroundColor: isEyedropperActive ? "#f1f3f5" : "#ffffff",
          borderRadius: 4,
          padding: "4px 6px",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Simple inline SVG pencil/eyedropper icon to avoid extra libraries */}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z"
            fill={isEyedropperActive ? "#228be6" : "#495057"}
          />
          <path
            d="M20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
            fill={isEyedropperActive ? "#228be6" : "#495057"}
          />
        </svg>
      </button>
    </div>
  );
} 

